import { lazy, Suspense } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Outlet } from 'react-router-dom';

import ErrorBoundary from '@/components/ErrorBoundary';

import 'dayjs/locale/ar';

// Remove AppLoading from here since it will be in MainLayout
import DocumentationLayout from '@/layouts/DocumentationLayout';
import { MainLayout } from '@/layouts/MainLayout'; // This now includes AppLoading
import { LoginStop } from '@/routes/Restrictions/LoginStop';
import PublicRoute from '@/routes/Restrictions/PublicRoute';
import PagePremission from '@/routes/Restrictions/PagePremission';
import AuthRequired from '@/routes/Restrictions/RequireAuth';
import AuthStatusCheck from '@/routes/Restrictions/AuthStatusCheck';
import AdminRoutes from '@/routes/Routers/AdminRoute';
import ItemRoutes from '@/routes/Routers/ItemRoute';
import PurchasesRoute from '@/routes/Routers/PurchasesRoute';
import SalesInvoicesRoute from '@/routes/Routers/SalesInvoicesRoute';
import BounsRoutes from '@/routes/Routers/BounsRoute';
import SettingRoutes from '@/routes/Routers/SettingRoute';
import SolovitRoutes from '@/routes/Routers/SolovitRoute';
import TargetSalesRoute, { TargetTeleSalesRoute } from '@/routes/Routers/TargetSalesRoute';
import AreaTargetRoutes from '@/routes/Routers/AreaTargetRoute';
import WarehousesRoutes from '@/routes/Routers/WarehousesRoute';
import TreasuryRoutes from '@/routes/Routers/TreasuryRoute';
import { useCheckPrems } from '@/utils/premissions/checkPrems';
import MasterPremission from '@/utils/premissions/MasterPremissions.enum';
import AppVersionPermission from '@/utils/premissions/AppVersionPermission.enum';
import GeneralSettingPermission from '@/utils/premissions/GeneralSettingPermission.enum';
import NotificationPermission from '@/utils/premissions/NotificationPermission.enum';
import MonitoringPermission from '@/utils/premissions/MonitoringPermission.enum';

// Simple fallback for Suspense - theme-aware
const SimpleFallback = () => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/90 backdrop-blur-sm">
    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
  </div>
);

// Updated withSuspense with simple fallback
const withSuspense =
  (Component: React.ComponentType<Record<string, unknown>>) =>
  (props: Record<string, unknown>) => (
    <ErrorBoundary>
      <Suspense fallback={<SimpleFallback />}>
        <Component {...props} />
      </Suspense>
    </ErrorBoundary>
  );

// Lazy-loaded page components
const HomePage = withSuspense(lazy(() => import('@/pages/Home')));
const LoginPage = withSuspense(lazy(() => import('@/pages/Status/Login')));
const ForgotPasswordPage = lazy(() => import('@/pages/Status/ForgotPassword'));
const ResetPasswordPage = lazy(() => import('@/pages/Status/ResetPassword'));
const NotFoundPage = withSuspense(
  lazy(() => import('@/pages/Status/Status404')),
);
const Status401 = lazy(() => import('@/pages/Status/Status401'));
const DocumentationPage = lazy(() => import('@/pages/Documentation'));
const ActivityLogPage = withSuspense(lazy(() => import('@/pages/ActivityLog')));
const ContactPage = withSuspense(lazy(() => import('@/pages/ContactUs')));
const TelesalesDashboardPage = withSuspense(
  lazy(() => import('@/pages/TelesalesDashboard')),
);
const SupervisorDashboardPage = withSuspense(
  lazy(() => import('@/pages/SupervisorDashboard')),
);
const AppVersionsPage = withSuspense(lazy(() => import('@/pages/AppVersions')));
const MobileVersionsPage = withSuspense(lazy(() => import('@/pages/MobileVersions')));
const GeneralSettingsPage = withSuspense(lazy(() => import('@/pages/GeneralSettings')));
const SendNotificationPage = withSuspense(lazy(() => import('@/pages/SendNotification')));
const MonitoringDashboardPage = withSuspense(lazy(() => import('@/pages/Monitoring')));

// Protected wrapper for Documentation page
const ProtectedDocumentationPage: React.FC = () => {
  const hasDocumentationPerm = useCheckPrems(
    MasterPremission.Prem_Master_Documantation,
  );

  if (!hasDocumentationPerm) {
    // Render Status401 directly without any layout constraints
    return (
      <Suspense fallback={<SimpleFallback />}>
        <Status401 />
      </Suspense>
    );
  }

  // Render with documentation layout when permission exists
  return (
    <DocumentationLayout>
      <DocumentationPage />
    </DocumentationLayout>
  );
};

// Layout wrappers
function AuthenticatedLayout() {
  return (
    <AuthRequired>
      {/* MainLayout now handles its own loading state with proper theme context */}
      <MainLayout>
        <Outlet />
      </MainLayout>
    </AuthRequired>
  );
}

function DocumentationPageLayout() {
  return <ProtectedDocumentationPage />;
}

// Route definitions
const router: RouteObject[] = [
  // Public routes (specific paths first)
  {
    path: '/login',
    element: (
      <LoginStop>
        <LoginPage />
      </LoginStop>
    ),
  },
  {
    path: '/login/from-logout',
    element: (
      <LoginPage
        key="logout-to-login"
        initialState={{ fromLogout: true, skipLoading: true }}
      />
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <PublicRoute skipAuthCheck={true}>
        <ForgotPasswordPage />
      </PublicRoute>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <PublicRoute skipAuthCheck={true}>
        <ResetPasswordPage />
      </PublicRoute>
    ),
  },
  {
    path: '/documentation',
    element: <DocumentationPageLayout />,
  },
  {
    path: '/not-auth',
    element: (
      <AuthStatusCheck>
        <Status401 />
      </AuthStatusCheck>
    ),
  },
  {
    path: '/contact',
    element: (
      <AuthStatusCheck>
        <ContactPage />
      </AuthStatusCheck>
    ),
  },
  // Authenticated routes (catch-all)
  {
    path: '/',
    element: <AuthenticatedLayout />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: 'hr/*',
        children: AdminRoutes,
      },
      {
        path: 'activity-log',
        element: (
          <PagePremission prem={MasterPremission.Prem_Activity_Log}>
            <ActivityLogPage />
          </PagePremission>
        ),
      },
      {
        path: 'app-versions',
        element: (
          <PagePremission prem={AppVersionPermission.Prem_App_Version_Views}>
            <AppVersionsPage />
          </PagePremission>
        ),
      },
      {
        path: 'mobile-versions',
        element: (
          <PagePremission prem={AppVersionPermission.Prem_App_Version_Views}>
            <MobileVersionsPage />
          </PagePremission>
        ),
      },
      {
        path: 'general-settings',
        element: (
          <PagePremission prem={GeneralSettingPermission.Prem_General_Setting_Open}>
            <GeneralSettingsPage />
          </PagePremission>
        ),
      },
      {
        path: 'send-notification',
        element: (
          <PagePremission prem={NotificationPermission.Prem_Notification_Send}>
            <SendNotificationPage />
          </PagePremission>
        ),
      },
      {
        path: 'monitoring',
        element: (
          <PagePremission
            prem={[
              MonitoringPermission.VIEW_OVERVIEW,
              MonitoringPermission.VIEW_SLOW_ENDPOINTS,
              MonitoringPermission.VIEW_ERRORS,
              MonitoringPermission.VIEW_PLATFORM_BREAKDOWN,
              MonitoringPermission.VIEW_REQUEST_DETAILS,
              MonitoringPermission.FULL_ACCESS,
            ]}
          >
            <MonitoringDashboardPage />
          </PagePremission>
        ),
      },
      {
        path: 'telesales-dashboard',
        element: <TelesalesDashboardPage />,
      },
      {
        path: 'supervisor-dashboard',
        element: <SupervisorDashboardPage />,
      },
      {
        path: 'settings/*',
        children: SettingRoutes,
      },
      {
        path: 'purchases/*',
        children: PurchasesRoute,
      },
      {
        path: 'items/*',
        children: ItemRoutes,
      },
      {
        path: 'sales-invoices/*',
        children: SalesInvoicesRoute,
      },
      {
        path: 'bouns/*',
        children: BounsRoutes,
      },
      {
        path: 'solovit/*',
        children: SolovitRoutes,
      },
      {
        path: 'target-sales/*',
        children: TargetSalesRoute,
      },
      {
        path: 'target-tele-sales/*',
        children: TargetTeleSalesRoute,
      },
      {
        path: 'area-target/*',
        children: AreaTargetRoutes,
      },
      {
        path: 'warehouses/*',
        children: WarehousesRoutes,
      },
      {
        path: 'treasury/*',
        children: TreasuryRoutes,
      },
    ],
  },
  // 404 catch-all
  {
    path: '*',
    element: <NotFoundPage />,
  },
];

export default router;
